import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';
import { usePracticalExerciseStore } from 'academy-common-lib/features/practicalExercises/practicalExerciseStore';
import UsageTracker from '../../../utils/UsageTracker';
import NotStartedPracticalExercise from './NotStartedPracticalExercise';
import InProgressPracticalExercise from './InProgressPracticalExercise';
import PracticalExerciseRequiredProduct from './PracticalExerciseRequiredProduct';
import PracticalExerciseRequiredAccess from './PracticalExerciseRequiredAccess';
import PracticalExerciseLoading from './PracticalExerciseLoading';
import PracticalExerciseError from './PracticalExerciseError';
import getLang from '../../../utils/getLang';
import PracticalExerciseWithSubmission from './PracticalExerciseWithSubmission';
import InReviewPracticalExerciseCondensed from './InReviewPracticalExerciseCondensed';
import CompletedPracticalExerciseCondensed from './CompletedPracticalExerciseCondensed';
import FailedPracticalExerciseCondensed from './FailedPracticalExerciseCondensed';
import { TrackingContentTypes } from '../../../constants/Tracking';
import { ContentProgressTypes, PracticalExerciseTypes } from '../../../constants/ContentConstants';
import { useContentStatus } from '../../userProgress/userProgressQueries';
import { useDeletePracticalExerciseProgress, useIsSimulationExercise, usePracticalExercise, usePracticalExerciseRequirements, useSetManuallyGradedPracticalExerciseWithAssetSubmitted, useSetPracticalExerciseStarted } from '../practicalExerciseQueries';
import { jsx as _jsx } from "react/jsx-runtime";
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;
export const PracticalExercise = ({
  trackId,
  lessonId,
  contentId,
  trackName,
  lessonName,
  language,
  onFinish
}) => {
  const {
    practicalExercise,
    practicalExerciseLoading,
    practicalExerciseError
  } = usePracticalExercise(Number(lessonId), Number(contentId), language);
  const {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  } = usePracticalExerciseRequirements(Number(contentId));
  const {
    contentStatus,
    contentStatusLoading,
    contentStatusError
  } = useContentStatus(Number(contentId), language);
  const [startPracticalExercise] = useSetPracticalExerciseStarted(language);
  const [submitPracticalExerciseWithAsset] = useSetManuallyGradedPracticalExerciseWithAssetSubmitted(language);
  const [deletePracticalExerciseProgress] = useDeletePracticalExerciseProgress();
  const {
    isSimulationExercise
  } = useIsSimulationExercise(Number(contentId), Number(trackId));
  const {
    startExerciseContext
  } = usePracticalExerciseStore();
  const track = action => {
    const graded = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.graded;
    const manuallyApproved = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.manuallyApproved;
    const name = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name;
    const trackerNotRequired = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.trackerNotRequired;
    const requiredProduct = practicalExerciseRequirements && practicalExerciseRequirements.requiredProducts && practicalExerciseRequirements.requiredProducts[0];
    const trackingMethod = trackerNotRequired ? UsageTracker.track : UsageTracker.trackExternalLink;
    trackingMethod.call(UsageTracker, 'contentInteraction', {
      action,
      contentId,
      contentLanguage: practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language,
      contentType: TrackingContentTypes.PRACTICAL_EXERCISE_FLOW,
      trackId,
      trackName,
      lessonId,
      lessonName,
      contentName: name,
      productName: requiredProduct || '',
      isUntrackedExercise: !!trackerNotRequired,
      graded: !!graded,
      manuallyApproved: !!manuallyApproved
    });
  };
  const handleClickThroughToProductPage = () => track('click-to-product-page');
  const setExerciseContext = simulationPortalId => {
    if (!practicalExercise) {
      return;
    }
    startExerciseContext({
      name: practicalExercise.name,
      description: practicalExercise.description,
      id: Number(contentId),
      exerciseLocation: practicalExercise.exerciseLocation || '',
      lessonId,
      trackId,
      portalId: simulationPortalId || PortalIdParser.getPortalIdFromPath(),
      sourcePortalId: PortalIdParser.getPortalIdFromPath(),
      isSimulation: isSimulationExercise,
      isGraded: practicalExercise.graded,
      isManuallyGraded: practicalExercise.manuallyApproved
    });
  };
  const handleStartExercise = async simulationPortalId => {
    setExerciseContext(simulationPortalId);
    track('click-start-exercise');
    await startPracticalExercise(Number(trackId), Number(lessonId), Number(contentId), simulationPortalId).catch(Raven.captureException.bind(Raven));
  };
  const handleTryExerciseAgain = simulationPortalId => setExerciseContext(simulationPortalId);
  const handleResumeExercise = simulationPortalId => {
    setExerciseContext(simulationPortalId);
    track('click-resume-exercise');
  };
  const handleSubmitManualExercise = (assetType, reference) => {
    track('click-review-exercise');
    submitPracticalExerciseWithAsset(Number(contentId), assetType, reference).catch(Raven.captureException);
  };
  const handleClickResubmit = () => {
    track('retry-practical-exercise');
    deletePracticalExerciseProgress(Number(contentId)).catch(Raven.captureException.bind(Raven));
  };
  const requiredProduct = practicalExerciseRequirements === null || practicalExerciseRequirements === void 0 ? void 0 : practicalExerciseRequirements.requiredProducts[0];
  const requiredAccess = practicalExerciseRequirements === null || practicalExerciseRequirements === void 0 ? void 0 : practicalExerciseRequirements.requiredAccess[0];
  const isManual = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.manuallyApproved;
  const error = practicalExerciseError || practicalExerciseRequirementsError || contentStatusError;
  const loading = practicalExerciseLoading || practicalExerciseRequirementsLoading || contentStatusLoading;
  const practicalExerciseType = practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.type;
  if (loading) {
    return /*#__PURE__*/_jsx(PracticalExerciseLoading, {});
  }
  if (error) {
    return /*#__PURE__*/_jsx(PracticalExerciseError, {
      error: error
    });
  }
  if (contentStatus === ContentProgressTypes.COMPLETED) {
    return /*#__PURE__*/_jsx(CompletedPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      allowRevisitExercise: practicalExerciseType === 'AUTOMATICALLY_GRADED',
      contentId: Number(contentId),
      exerciseLocation: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.exerciseLocation) || '',
      handleSetExerciseContext: handleTryExerciseAgain,
      language: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language) || language,
      lessonId: lessonId,
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.IN_REVIEW) {
    return /*#__PURE__*/_jsx(InReviewPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      isManual: !!isManual,
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      lessonId: lessonId,
      onResubmitClick: handleClickResubmit,
      resubmitType: "resubmit",
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.STARTED && practicalExerciseType !== PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
    return /*#__PURE__*/_jsx(InProgressPracticalExercise, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      exercise: practicalExercise,
      exerciseLocation: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.exerciseLocation) || '',
      language: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.language) || getLang(),
      lessonId: lessonId,
      onResumeExercise: handleResumeExercise,
      trackId: trackId
    });
  }
  if (contentStatus === ContentProgressTypes.FAILED) {
    return /*#__PURE__*/_jsx(FailedPracticalExerciseCondensed, {
      advanceToNextContent: onFinish,
      contentId: Number(contentId),
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      lessonId: lessonId,
      onResubmitClick: handleClickResubmit,
      resubmitType: "resubmit",
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || '',
      trackId: trackId
    });
  }
  if (requiredProduct) {
    return /*#__PURE__*/_jsx(PracticalExerciseRequiredProduct, {
      advanceToNextContent: onFinish,
      onClickProductLink: handleClickThroughToProductPage,
      portalId: PortalIdParser.getPortalIdFromPath(),
      requiredProduct: requiredProduct
    });
  }
  if (requiredAccess) {
    return /*#__PURE__*/_jsx(PracticalExerciseRequiredAccess, {
      advanceToNextContent: onFinish,
      product: requiredAccess
    });
  }
  if (practicalExerciseType === PracticalExerciseTypes.MANUALLY_GRADED_WITH_FILE_SUBMISSION) {
    return /*#__PURE__*/_jsx(PracticalExerciseWithSubmission, {
      advanceToNextContent: onFinish,
      isPassRequiredForCert: !!(practicalExercise !== null && practicalExercise !== void 0 && practicalExercise.graded),
      isSubmitting: false,
      onSubmit: handleSubmitManualExercise,
      title: (practicalExercise === null || practicalExercise === void 0 ? void 0 : practicalExercise.name) || ''
    });
  }
  return practicalExercise && /*#__PURE__*/_jsx(NotStartedPracticalExercise, {
    advanceToNextContent: onFinish,
    contentId: Number(contentId),
    exercise: practicalExercise,
    exerciseLocation: practicalExercise.exerciseLocation || '',
    language: practicalExercise.language,
    lessonId: Number(lessonId),
    onStartClick: handleStartExercise,
    trackId: Number(trackId)
  });
};